import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Filter, RotateCcw, Search } from "lucide-react";
import axios from "axios";
import { useAuth } from "../../App";
import { useToast } from "@/hooks/use-toast";

/**
 * FilterBar — the shared filter shell used by every CRM list page.
 *
 * Structure:
 *   ┌─ search input (optional) ─┬─ children (filter selects) ─┬─ CSV / Reset ─┐
 *
 * The consumer wires each child (typically a MultiSelectFilter) to its
 * own value/onChange from `useUrlFilters`. This component only owns
 * layout, the search input, the reset button, and the CSV export.
 */
export function FilterBar({
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search…",
  onReset,
  activeCount = 0,
  csvUrl,
  csvFilename = "export.csv",
  children,
  testId = "filter-bar",
  hideSearch = false,
}) {
  const { token } = useAuth();
  const { toast } = useToast();

  const downloadCsv = async () => {
    if (!csvUrl) return;
    try {
      const res = await axios.get(csvUrl, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = csvFilename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({ title: "CSV downloaded" });
    } catch (e) {
      toast({
        title: "Export failed",
        description: e?.response?.status ? `HTTP ${e.response.status}` : e.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div
      className="bg-white rounded-lg border border-slate-200 p-3 mb-4 flex flex-col lg:flex-row gap-3 lg:items-center"
      data-testid={testId}
    >
      {!hideSearch && (
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <Input
            placeholder={searchPlaceholder}
            className="pl-9 h-9"
            value={searchValue || ""}
            onChange={(e) => onSearchChange?.(e.target.value)}
            data-testid={`${testId}-search`}
          />
        </div>
      )}
      <div className="flex gap-2 flex-wrap items-center">
        {children}
      </div>
      <div className="flex items-center gap-2 lg:ml-auto shrink-0">
        {activeCount > 0 && (
          <span
            className="text-xs px-2 py-1 bg-orange-50 text-[#DE6E4B] rounded-md flex items-center gap-1"
            data-testid={`${testId}-active-count`}
          >
            <Filter className="w-3 h-3" /> {activeCount} filter{activeCount === 1 ? "" : "s"}
          </span>
        )}
        {activeCount > 0 && onReset && (
          <Button
            variant="ghost"
            size="sm"
            className="h-9 px-2 text-slate-500 hover:text-slate-900"
            onClick={onReset}
            data-testid={`${testId}-reset`}
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
          </Button>
        )}
        {csvUrl && (
          <Button
            variant="outline"
            size="sm"
            className="h-9"
            onClick={downloadCsv}
            data-testid={`${testId}-csv`}
          >
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
        )}
      </div>
    </div>
  );
}

export default FilterBar;
